// EAA-1667: Pure JWT-shape heuristic, moved from web-content's embedApiToken util
// into the core auth module so any app (SSO/embed flows) can reuse it. Not a real
// verification — just a cheap "does this look like a JWT" check to route a raw token
// to the right Authorization scheme.

/** True if `value` looks like a JWT (3 dot-separated segments, header starts `eyJ`). */
export function isLikelyJwt(value: string): boolean {
    const v = value.trim();
    if (!v) return false;
    const parts = v.split('.');
    if (parts.length !== 3) return false;
    return parts[0].startsWith('eyJ');
}

/**
 * EAA-1762: epoch milliseconds of the token's `exp` claim, or `null` when the
 * token is unreadable or carries no `exp`.
 *
 * Nothing in the app decoded `exp` before this: password mode has no silent
 * renew (`authClient` is a stub there), so a stored access token was handed to
 * every request until the server rejected it. Waking a long-idle tab therefore
 * always burned a guaranteed 401 first, which put the whole refresh-and-retry
 * path — including its failure modes — on the critical path of the first
 * screen the user sees.
 */
export function jwtExpiresAt(token: string): number | null {
    if (!isLikelyJwt(token)) return null;
    const payload = token.split('.')[1];
    try {
        const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
        const claims = JSON.parse(json) as { exp?: unknown };
        return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
    } catch {
        return null;
    }
}

/**
 * True when `token` is already expired or expires within `skewMs`. A token we
 * cannot read returns `false` — an opaque token is the server's business, and
 * guessing "expired" would refresh on every request.
 */
export function isJwtExpiring(token: string, skewMs = 60_000): boolean {
    const expiresAt = jwtExpiresAt(token);
    if (expiresAt === null) return false;
    return expiresAt - Date.now() <= skewMs;
}
